import type {RootState} from 'store/types';
import {getMockCodeListSync} from 'api/mockCodeLists/getMockCodeList';
import type {CodeList, CodeListId} from './types';
import {CombinedCodeListIds, PartCodeLists} from './types';
import {codelistApi} from './codelistApi';
import {mockCodeLists} from './consts';

const mockCache: Record<string, CodeList> = {};

const getMockCodelist = (
  id: CodeListId,
  language: string,
): CodeList | undefined => {
  if (!mockCodeLists.includes(id)) {
    return undefined;
  }

  const key = `${id}::${language}`;
  if (!mockCache[key]) {
    mockCache[key] = getMockCodeListSync(id, language);
  }
  return mockCache[key];
};

const combinedCache: Record<
  string,
  {parts: ReadonlyArray<CodeList>; combined: CodeList}
> = {};

const getCombinedCodelist = (
  id: CodeListId,
  language: string,
  state: RootState,
): CodeList | undefined => {
  if (!CombinedCodeListIds.includes(id as never)) {
    return undefined;
  }

  const partIds = PartCodeLists[id as keyof typeof PartCodeLists];
  const parts: CodeList[] = [];
  for (const partId of partIds) {
    const part = selectCodelistFromCache(partId, language, state);
    if (!part) {
      return undefined;
    }
    parts.push(part);
  }

  const key = `${id}::${language}`;
  const cached = combinedCache[key];
  if (
    cached &&
    cached.parts.length === parts.length &&
    cached.parts.every((p, i) => p === parts[i])
  ) {
    return cached.combined;
  }

  const combined: CodeList = {
    id,
    language,
    codes: parts.flatMap(p => p.codes),
  };
  combinedCache[key] = {parts, combined};

  return combined;
};

/**
 * Reads a codelist by id from either the mock data, the combined-codelist
 * synthesizer, or the RTK Query cache for `getCodelist`. Returns undefined
 * if the codelist has not been loaded yet (or is not a known mock/combined).
 */
export const selectCodelistFromCache = (
  id: CodeListId,
  language: string,
  state: RootState,
): CodeList | undefined => {
  const mock = getMockCodelist(id, language);
  if (mock) {
    return mock;
  }

  const combined = getCombinedCodelist(id, language, state);
  if (combined) {
    return combined;
  }

  return codelistApi.endpoints.getCodelist.select({id, language})(state).data;
};
